import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterLink } from '@angular/router';
import {
  DpeState,
  EntityStatus,
  GouvGeorisquesDto,
  PatchEntityStateDto,
  PropertyAddMediaDto,
  PropertyCharacteristicsDto,
  PropertyConstructionDto,
  PropertyCreationStep,
  PropertyDpeDto,
  PropertyDto,
  PropertyEquipment,
  PropertyFinancialsDto,
  PropertyGeolocationType,
  PropertyKeyInfoDto,
  PropertyLocationDto,
  PropertyMandateDto,
  PropertyMediaDto,
  PropertyPhotoDto,
  PropertyPresentationDto,
  PropertyVerificationStatus,
  UserApplications,
} from '@workspace/data-types';
import {
  AlertSnackBarService,
  DtoChangedDetection,
  EntityStateDialogAuraComponent,
} from '@workspace/ng-aura';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  getPropertyEquipmentInfo,
  PROPERTY_EQUIPMENT_ITEMS,
  PropertyEquipmentItem,
} from '../property-characteristics/equipments-list/property-equipment.constants';

import { canUserEditProperty } from '../../utils/property-permission.utils';

import { PropertyAssociatedUsersComponent } from '../property-associated-users/property-associated-users.component';
import { PropertyCharacteristicsEditComponent } from '../property-characteristics/main/edit/characteristics-edit.component';
import { PropertyConstructionEditComponent } from '../property-construction/edit/construction-edit.component';
import { PropertyDefinitionDisplayComponent } from '../property-definition/display/definition-display.component';
import { PropertyDocumentsEditComponent } from '../property-documents/property-documents-edit.component';
import { PropertyDpeEditComponent } from '../property-dpe/main/edit/dpe-edit.component';
import { PropertyFinancialsEditComponent } from '../property-financials/edit/financials-edit.component';
import { PropertyCadastralCardComponent, PropertyGeospatialDisplayComponent } from '../property-geospatial';
import { PropertyKeyInfosEditComponent } from '../property-key-infos/main/edit/key-infos-edit.component';
import { PropertyLocationEditComponent } from '../property-location/edit/location-edit.component';
import { PropertyMandateEditComponent } from '../property-mandate/edit/mandate-edit.component';
import { PropertyModerationDisplayComponent } from '../property-moderation/property-moderation-display.component';
import { PropertyPhotosEditComponent } from '../property-photos/edit/photos-edit.component';
import { PropertyPresentationEditComponent } from '../property-presentation/edit/presentation-edit.component';
import {
  EtxePropertySectionNavComponent,
  getPropertyNavSections,
  PropertyNavSection,
} from '../property-section-nav/property-section-nav.component';
import { EtxePropertyStatusHeaderComponent } from '../status-header/property-status-header';

@Component({
  selector: 'etxe-property-details',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    PropertyDefinitionDisplayComponent,
    PropertyPhotosEditComponent,
    PropertyPresentationEditComponent,
    PropertyLocationEditComponent,
    PropertyCadastralCardComponent,
    PropertyFinancialsEditComponent,
    PropertyMandateEditComponent,
    PropertyKeyInfosEditComponent,
    PropertyCharacteristicsEditComponent,
    PropertyConstructionEditComponent,
    PropertyDpeEditComponent,
    PropertyGeospatialDisplayComponent,
    PropertyDocumentsEditComponent,
    PropertyModerationDisplayComponent,
    PropertyAssociatedUsersComponent,
    EtxePropertyStatusHeaderComponent,
    EtxePropertySectionNavComponent,
    RouterLink,
  ],
  templateUrl: './property-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full' },
})
export class EtxePropertyDetailsComponent {
  private api = inject(ApiService);
  private router = inject(Router);
  private alertSnackbar = inject(AlertSnackBarService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private appStore = inject(ApplicationStore);
  private userStore = inject(UserStore);

  // Use Angular Signal input
  property = input.required<PropertyDto>();
  editable = input<boolean>(true);

  // Output event when property gets updated successfully
  propertyUpdated = output<PropertyDto>();

  // Determine if the current context is Admin (Admin application + Admin role)
  readonly isAdmin = computed(
    () => this.appStore.name() === UserApplications.ADMIN && this.userStore.isAdmin(),
  );

  // Determine if user can edit this property (Admin or Owner/Co-owner)
  readonly canEdit = computed(() => {
    if (this.isAdmin()) return true;
    return this.editable() && canUserEditProperty(this.property(), this.userStore.current());
  });

  // Compute navigation sections according to user role and mandate presence
  readonly navSections = computed<PropertyNavSection[]>(() => {
    return getPropertyNavSections({
      hasMandate: !!this.property()?.mandate,
      hasSettings: true,
      isAdmin: this.isAdmin(),
      moderationBadge: this.property()?.moderation?.history?.length || undefined,
    });
  });

  goToSettings(): void {
    if (this.isAdmin()) {
      this.scrollToSection('settings-section');
    } else {
      const prop = this.property();
      if (prop) {
        void this.router.navigate(['/properties', prop._id, 'settings']);
      }
    }
  }

  openAdminStateDialog(status: EntityStatus): void {
    const property = this.property();
    if (!property) return;

    this.dialog
      .open(EntityStateDialogAuraComponent, {
        data: {
          status,
          label: 'Ce bien',
          isAdmin: true,
          deletionConfirmation: status === EntityStatus.DELETED,
        },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result): result is PatchEntityStateDto => !!result),
        switchMap((patchState) => {
          const adminProps = this.api.business().etxe().admin().properties();
          if (status === EntityStatus.ACTIVE) {
            return adminProps.activate(property._id);
          } else if (status === EntityStatus.DEACTIVATED) {
            return adminProps.deactivate(property._id, patchState);
          } else if (status === EntityStatus.DELETED) {
            return adminProps.deleteState(property._id, patchState).pipe(
              map(() => ({
                ...property,
                state: { ...property.state, status: EntityStatus.DELETED },
              })),
            );
          }
          return of(property);
        }),
        tap((updatedProperty) => {
          this.propertyUpdated.emit(updatedProperty as PropertyDto);
          this.alertSnackbar.success({
            title: 'Statut mis à jour',
            message: 'Le statut du bien a été modifié avec succès.',
          });
        }),
        catchError((err) => {
          console.error('Error updating property state:', err);
          this.alertSnackbar.error({
            title: 'Erreur',
            message: 'Impossible de mettre à jour le statut du bien.',
          });
          return of(null);
        }),
      )
      .subscribe();
  }

  // Enum & Step references for the template
  EntityStatus = EntityStatus;
  PropertyCreationStep = PropertyCreationStep;
  PropertyVerificationStatus = PropertyVerificationStatus;
  DpeState = DpeState;
  readonly CONSTRUCTION_STEP = PropertyCreationStep.CONSTRUCTION;
  readonly MANDATE_STEP = 'mandate';

  cadastralExpanded = signal<boolean>(true);

  verificationStatus = computed(
    () => this.property()?.verification?.owner ?? PropertyVerificationStatus.UNVERIFIED,
  );
  isVerified = computed(() => this.verificationStatus() === PropertyVerificationStatus.VERIFIED);

  scrollToDocuments(): void {
    this.scrollToSection('documents-section');
  }

  scrollToSection(anchorId: string): void {
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onHeaderEditRequested(event: { step?: PropertyCreationStep; anchorId: string }): void {
    if (event.step != null) {
      this.startEdit(event.step);
    }
    setTimeout(() => {
      const el = document.getElementById(event.anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }

  // Track which section is currently in editing mode
  editingSection = signal<PropertyCreationStep | string | null>(null);

  // Loading state mapping by step
  savingState = signal<Record<string | number, boolean>>({});

  // Internal buffer for changes during edit, keyed by section step enum or string
  private changesBuffer: Record<string | number, unknown> = {};

  getMapRadius(type?: PropertyGeolocationType | null): number {
    switch (type) {
      case PropertyGeolocationType.NEIGHBORHOOD:
        return 800;
      case PropertyGeolocationType.CITY:
        return 2000;
      case PropertyGeolocationType.REGION:
        return 10000;
      default:
        return 800;
    }
  }

  startEdit(step: PropertyCreationStep | string) {
    if (step === PropertyCreationStep.DEFINITION) {
      // Per user explicit instruction, Definition cannot be edited.
      return;
    }
    this.editingSection.set(step);
    this.changesBuffer[step] = null; // Clear old buffer
  }

  cancelEdit() {
    this.editingSection.set(null);
  }

  onPropertyDocumentsUpdated(updated: PropertyDto) {
    this.propertyUpdated.emit(updated);
  }

  onDtoChanged(step: PropertyCreationStep | string, event: DtoChangedDetection<unknown>) {
    // Only capture the updated dto here
    this.changesBuffer[step] = event.dto;
  }

  saveSection(step: PropertyCreationStep | string) {
    const dtoToSave = this.changesBuffer[step];

    const currentState = this.savingState();
    this.savingState.set({ ...currentState, [step]: true });

    const propertyId = this.property()._id;
    let action$: Observable<unknown>;

    if (this.isAdmin() && step === PropertyCreationStep.PHOTOS) {
      action$ = this.api
        .business()
        .etxe()
        .admin()
        .properties()
        .patchPhotos(propertyId, dtoToSave as PropertyAddMediaDto);
    } else {
      const patchApi = this.api.business().etxe().properties().owner().patch(propertyId);
      switch (step) {
        case PropertyCreationStep.LOCATION:
          action$ = patchApi.location(dtoToSave as PropertyLocationDto);
          break;
        case PropertyCreationStep.CONSTRUCTION:
          action$ = patchApi.construction(dtoToSave as PropertyConstructionDto);
          break;
        case PropertyCreationStep.KEY_INFOS:
          action$ = patchApi.keyInfo(dtoToSave as PropertyKeyInfoDto);
          break;
        case PropertyCreationStep.CHARACTERISTICS:
          action$ = patchApi.characteristics(dtoToSave as PropertyCharacteristicsDto);
          break;
        case PropertyCreationStep.FINANCIALS:
          action$ = patchApi.financials(dtoToSave as PropertyFinancialsDto);
          break;
        case this.MANDATE_STEP:
          action$ = patchApi.mandate(dtoToSave as PropertyMandateDto);
          break;
        case PropertyCreationStep.DPE:
          action$ = patchApi.dpe(dtoToSave as PropertyDpeDto);
          break;
        case PropertyCreationStep.GEOHAZARDS:
          action$ = patchApi.geohazards();
          break;
        case PropertyCreationStep.PHOTOS:
          action$ = patchApi.media(dtoToSave as PropertyMediaDto);
          break;
        case PropertyCreationStep.PRESENTATION:
          action$ = patchApi.presentation(dtoToSave as PropertyPresentationDto);
          break;
        default:
          return;
      }
    }

    action$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((updatedSection) => {
          const current = this.property();
          const mergedProperty: PropertyDto = { ...current };

          switch (step) {
            case PropertyCreationStep.LOCATION:
              mergedProperty.location =
                (dtoToSave as PropertyLocationDto) || (updatedSection as PropertyLocationDto);
              break;
            case PropertyCreationStep.CONSTRUCTION:
              mergedProperty.construction =
                (dtoToSave as PropertyConstructionDto) || (updatedSection as PropertyConstructionDto);
              break;
            case PropertyCreationStep.KEY_INFOS:
              mergedProperty.keyInfo =
                (dtoToSave as PropertyKeyInfoDto) || (updatedSection as PropertyKeyInfoDto);
              break;
            case PropertyCreationStep.CHARACTERISTICS:
              mergedProperty.characteristics =
                (dtoToSave as PropertyCharacteristicsDto) || (updatedSection as PropertyCharacteristicsDto);
              break;
            case PropertyCreationStep.FINANCIALS:
              mergedProperty.financials =
                (dtoToSave as PropertyFinancialsDto) || (updatedSection as PropertyFinancialsDto);
              break;
            case this.MANDATE_STEP:
              mergedProperty.mandate =
                (dtoToSave as PropertyMandateDto) || (updatedSection as PropertyMandateDto);
              break;
            case PropertyCreationStep.DPE:
              mergedProperty.dpe = (dtoToSave as PropertyDpeDto) || (updatedSection as PropertyDpeDto);
              break;
            case PropertyCreationStep.GEOHAZARDS:
              mergedProperty.geospatial = {
                ...mergedProperty.geospatial,
                geohazard: updatedSection as GouvGeorisquesDto,
              };
              break;
            case PropertyCreationStep.PHOTOS: {
              const savedPhotos = (dtoToSave as PropertyAddMediaDto)?.photos || [];
              mergedProperty.media = {
                coverPhoto: savedPhotos[0]?.file,
                photos: savedPhotos as unknown as PropertyPhotoDto[],
              };
              break;
            }
            case PropertyCreationStep.PRESENTATION:
              mergedProperty.presentation =
                (dtoToSave as PropertyPresentationDto) || (updatedSection as PropertyPresentationDto);
              break;
          }

          this.propertyUpdated.emit(mergedProperty);
          this.editingSection.set(null);
          this.savingState.set({ ...this.savingState(), [step]: false });
          this.alertSnackbar.success({ message: 'Section sauvegardée avec succès' });
        }),
        catchError((err) => {
          console.error('Error saving section:', err);
          this.alertSnackbar.error({ message: 'Erreur lors de la sauvegarde' });
          this.savingState.set({ ...this.savingState(), [step]: false });
          return of(null);
        }),
      )
      .subscribe();
  }

  // Computed signal for total interior surface
  totalInteriorSurface = computed(() => {
    const interior = this.property()?.characteristics?.interior;
    if (!interior) return 0;

    const sum = (arr: { surface?: number }[] = []) => arr.reduce((acc, curr) => acc + (curr.surface || 0), 0);

    return sum(interior.rooms) + sum(interior.bedroom) + sum(interior.bathroom) + sum(interior.showerRoom);
  });

  // Computed signal for total exterior surface
  totalExteriorSurface = computed(() => {
    const exterior = this.property()?.characteristics?.exterior;
    if (!exterior) return 0;

    const sum = (arr: { surface?: number }[] = []) => arr.reduce((acc, curr) => acc + (curr.surface || 0), 0);

    return (
      sum(exterior.garden) +
      sum(exterior.terrace) +
      sum(exterior.balcony) +
      sum(exterior.loggia) +
      sum(exterior.swimmingPool) +
      sum(exterior.cellar)
    );
  });

  // Computed signal for total common areas surface
  totalCommonAreasSurface = computed(() => {
    const areas = this.property()?.characteristics?.commonAreas;
    if (!areas) return 0;

    return areas.reduce((acc, curr) => acc + (curr.surface || 0), 0);
  });

  // Computed signal for total parking surface
  totalParkingSurface = computed(() => {
    const parkings = this.property()?.characteristics?.parkings;
    if (!parkings) return 0;

    return parkings.reduce((acc, curr) => acc + (curr.totalSurface || 0), 0);
  });

  equipmentItems: PropertyEquipmentItem[] = PROPERTY_EQUIPMENT_ITEMS;

  getEquipmentInfo(field: PropertyEquipment) {
    return getPropertyEquipmentInfo(field);
  }

  isRiskPresent(risk: { present?: boolean } | null | undefined): boolean {
    return risk?.present === true;
  }
}
